import React, { useContext, useEffect, useState } from 'react';
import { Box, Grid, Typography, Divider } from '@mui/material';
import ThermostatRoundedIcon    from '@mui/icons-material/ThermostatRounded';
import WaterDropRoundedIcon     from '@mui/icons-material/WaterDropRounded';
import Co2RoundedIcon           from '@mui/icons-material/Co2Rounded';
import AirRoundedIcon           from '@mui/icons-material/AirRounded';
import CompressRoundedIcon      from '@mui/icons-material/CompressRounded';
import GrassRoundedIcon         from '@mui/icons-material/GrassRounded';
import axios from 'axios';
import { useGrowSetup }  from '../../GrowSetupContext';
import { AuthContext }   from '../../components/signin/AuthContext';
import SensorCard        from '../components/SensorCard';
import ActorCard         from '../components/ActorCard';

function isNowBetween(s, e) {
  if (!s || !e) return true;
  const [sh, sm] = s.split(':').map(Number);
  const [eh, em] = e.split(':').map(Number);
  const now   = new Date();
  const start = new Date(now); start.setHours(sh, sm, 0, 0);
  const end   = new Date(now); end.setHours(eh, em, 0, 0);
  return start <= end ? now >= start && now <= end : now >= start || now <= end;
}

const SENSORS = [
  { measure: 'temperatur',   title: 'Temperatur',        einheit_zeichen: ' °C', ideal_val: 22 },
  { measure: 'temperatur_2', title: 'Temperatur 2',      einheit_zeichen: ' °C', ideal_val: 22 },
  { measure: 'humidity',     title: 'Luftfeuchte',       einheit_zeichen: ' %',  ideal_val: 65 },
  { measure: 'humidity_2',   title: 'Luftfeuchte 2',     einheit_zeichen: ' %',  ideal_val: 65 },
  { measure: 'co2',          title: 'CO₂',               einheit_zeichen: ' ppm',ideal_val: 1100 },
  { measure: 'vpd',          title: 'VPD',               einheit_zeichen: '',    ideal_val: 0.8 },
  { measure: 'luftdruck',    title: 'Luftdruck',         einheit_zeichen: ' hPa',ideal_val: 980 },
  { measure: 'bodenfeuchte_1', title: 'Bodenfeuchte 1',  einheit_zeichen: ' %',  ideal_val: 60  },
  { measure: 'bodenfeuchte_2', title: 'Bodenfeuchte 2',  einheit_zeichen: ' %',  ideal_val: 60  },
  { measure: 'bodenfeuchte_3', title: 'Bodenfeuchte 3',  einheit_zeichen: ' %',  ideal_val: 60  },
  { measure: 'bodenfeuchte_4', title: 'Bodenfeuchte 4',  einheit_zeichen: ' %',  ideal_val: 60  },
];

const ACTORS = [
  { measure: 'licht',   title: 'Licht'   },
  { measure: 'abluft',  title: 'Abluft'  },
  { measure: 'heizung', title: 'Heizung' },
  { measure: 'umluft',  title: 'Umluft'  },
];

const DEVICES = [
  { measure: 'bme', title: 'Sensor BME', einheit_zeichen: '', ideal_val: null },
  { measure: 'sht', title: 'Sensor SHT', einheit_zeichen: '', ideal_val: null },
];

function SectionHeader({ children }) {
  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <Typography variant="overline" sx={{ color: '#76c442', fontWeight: 700, letterSpacing: '0.12em', fontSize: '0.7rem' }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5, borderColor: 'rgba(118,196,66,0.15)' }} />
    </Box>
  );
}

export default function DashboardPage() {
  const { grow_setup }   = useGrowSetup();
  const { token }        = useContext(AuthContext);
  const [gauges, setGauges] = useState({});
  const [sensors, setSensors] = useState(SENSORS);

  // fetch live gauge values
  useEffect(() => {
    if (!grow_setup) return;
    const load = () => {
      axios.post(`/get_current_gauge_values/${grow_setup}`, { hello: 'world' }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }).then(r => setGauges(r.data || {})).catch(console.error);
    };
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [grow_setup]);

  // fetch ideal values from system config
  useEffect(() => {
    if (!grow_setup) return;
    axios.post(`/get_sys_config_grow_setup/${grow_setup}`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const d   = res.data;
      const day = isNowBetween(d.sun_rise, d.sun_set);
      setSensors(prev => prev.map(s => {
        if (s.measure === 'temperatur' || s.measure === 'temperatur_2')
          return { ...s, ideal_val: parseFloat(day ? d.temperatur_tag : d.temperatur_nacht) };
        if (s.measure === 'humidity' || s.measure === 'humidity_2')
          return { ...s, ideal_val: parseFloat(day ? d.luftfeuchte_tag : d.luftfeuchte_nacht) };
        if (s.measure === 'co2')
          return { ...s, ideal_val: parseFloat(d.co2_zielwert) };
        return s;
      }));
    }).catch(console.error);
  }, [grow_setup]);

  return (
    <Box>
      {/* Sensors */}
      <SectionHeader>Sensoren</SectionHeader>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {sensors.map((s, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={s.measure}>
            <SensorCard
              measure={s.measure}
              title={s.title}
              trend="neutral"
              ideal_val={s.ideal_val}
              gauge_value={gauges[s.measure]}
              einheit_zeichen={s.einheit_zeichen}
              animDelay={i * 55}
            />
          </Grid>
        ))}
      </Grid>

      {/* Actors */}
      <SectionHeader>Aktoren</SectionHeader>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {ACTORS.map((a, i) => (
          <Grid item xs={12} sm={6} md={3} key={a.measure}>
            <ActorCard
              measure={a.measure}
              title={a.title}
              trend="neutral"
              animDelay={i * 55}
            />
          </Grid>
        ))}
      </Grid>

      {/* Devices */}
      <SectionHeader>Geräte</SectionHeader>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {DEVICES.map((d, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={d.measure}>
            <SensorCard
              measure={d.measure}
              title={d.title}
              trend="neutral"
              ideal_val={null}
              gauge_value={gauges[d.measure]}
              einheit_zeichen={d.einheit_zeichen}
              animDelay={i * 55}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
